import React, { useState, useRef, useEffect, useCallback } from "react";
import type { MenuProps } from "antd";
import TrebbleDropdown from "../common/TrebbleDropdown";
import TrebbleTooltip from "../common/TrebbleTooltip";
import { PROJECT_CARD_MENU_ACTIONS, ProjectCardMenuAction } from "./LibraryItemTypes";
import { getCloudFrontUrl } from "../helper/CloudFrontUrlHelper";
import "./styles.css";

interface ProjectCardCreator {
  name: string;
  initials: string;
  color: string;
}

interface ProjectCardProps {
  id: string;
  capsuleId?: string;   // actual Capsule ID for thumbnail API calls (distinct from display id)
  name: string;
  type: "audio" | "video" | "image" | "vector";
  duration?: string;
  date: string;
  creator?: ProjectCardCreator;
  thumbnailColor?: string;
  thumbnailUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  previewUrl?: string;
  status?: "ready" | "processing";
  viewMode?: "grid" | "list";
  onPlay: (id: string) => void;
  onDownload: (id: string) => void;
  onMenuAction: (id: string, action: string) => void;
  onClick: (id: string) => void;
  ti18n?: Record<string, string>;
  showDownloadButton?: boolean;
  menuActions?: ProjectCardMenuAction[];
  primaryActionLabel?: string;
  primaryActionIconClassName?: string;
}

const t = (ti18n: Record<string, string> | undefined, key: string): string => {
  if (ti18n) {
    return window.getI18n(ti18n, key);
  }
  return key;
};

const PlayIcon: React.FC = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M5 3l10 6-10 6V3z" fill="#fff" />
  </svg>
);

const PauseIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="2" y="1" width="3.5" height="12" rx="0" fill="#fff" />
    <rect x="8.5" y="1" width="3.5" height="12" rx="0" fill="#fff" />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg
    width={12}
    height={12}
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 2l8 8M10 2l-8 8"
      stroke="#fff"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const DownloadIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ color: "var(--text-default-color)" }}
  >
    <path
      d="M7 1v8M7 9L4 6M7 9l3-3M2 12h10"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MoreIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ color: "var(--text-default-color)" }}
  >
    <circle cx="7" cy="3" r="1.2" fill="currentColor" />
    <circle cx="7" cy="7" r="1.2" fill="currentColor" />
    <circle cx="7" cy="11" r="1.2" fill="currentColor" />
  </svg>
);

const getFinitePlaybackTime = (seconds: number | null | undefined): number | null => {
  return typeof seconds === "number" && Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
};

const parseDurationLabel = (duration?: string): number | null => {
  if (!duration) return null;
  const parts = duration.split(":").map((part) => Number(part));
  if (!parts.length || parts.some((part) => !Number.isFinite(part) || part < 0)) return null;
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return (parts[0] * 60) + parts[1];
  if (parts.length === 3) return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
  return null;
};

const formatTime = (seconds: number | null | undefined): string => {
  const finiteSeconds = getFinitePlaybackTime(seconds) || 0;
  const mins = Math.floor(finiteSeconds / 60);
  const secs = Math.floor(finiteSeconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const getSeekableDuration = (media: HTMLMediaElement): number | null => {
  if (media.seekable.length === 0) return null;
  try {
    return getFinitePlaybackTime(media.seekable.end(media.seekable.length - 1));
  } catch (_error) {
    return null;
  }
};

const getMediaElementDuration = (
  media: HTMLMediaElement,
  fallbackDuration: number | null,
  currentPlaybackTime?: number,
  useSeekableDuration = false,
): number | null => {
  const mediaDuration = getFinitePlaybackTime(media.duration);
  if (mediaDuration && mediaDuration > 0) return mediaDuration;

  const currentTime = getFinitePlaybackTime(currentPlaybackTime ?? media.currentTime) || 0;
  const bestKnownDuration = (useSeekableDuration ? getSeekableDuration(media) : null) || fallbackDuration;
  if (bestKnownDuration && bestKnownDuration >= currentTime - 0.5) {
    return bestKnownDuration;
  }

  return null;
};

const waitForMediaEvent = (
  media: HTMLMediaElement,
  events: string[],
  timeoutMs: number,
): Promise<void> => new Promise((resolve) => {
  let settled = false;
  let timeoutId: number | undefined;
  const cleanup = () => {
    if (settled) return;
    settled = true;
    if (timeoutId) window.clearTimeout(timeoutId);
    events.forEach((eventName) => media.removeEventListener(eventName, onEvent));
    resolve();
  };
  const onEvent = () => cleanup();
  events.forEach((eventName) => media.addEventListener(eventName, onEvent, { once: true }));
  timeoutId = window.setTimeout(cleanup, timeoutMs);
});

const isRawS3VideoUrl = (videoUrl: string): boolean => {
  try {
    const parsedUrl = new URL(videoUrl);
    return parsedUrl.hostname === "amazonaws.com" || parsedUrl.hostname.endsWith(".amazonaws.com");
  } catch (_error) {
    return false;
  }
};

const computeMediaDurationFromFile = async (mediaUrl: string): Promise<number | null> => {
  try {
    const response = await fetch(getCloudFrontUrl(mediaUrl));
    if (!response.ok) return null;
    const mediaBlob = await response.blob();
    const { Input, BlobSource, ALL_FORMATS } = await import("mediabunny");
    const input = new Input({
      source: new BlobSource(mediaBlob),
      formats: ALL_FORMATS,
    });
    try {
      return getFinitePlaybackTime(await input.computeDuration());
    } finally {
      input.dispose();
    }
  } catch (_error) {
    return null;
  }
};

const discoverFiniteMediaDuration = async (
  media: HTMLMediaElement,
  mediaUrl: string,
  fallbackDuration: number | null,
): Promise<number | null> => {
  const parsedDurationPromise = computeMediaDurationFromFile(mediaUrl);
  await waitForMediaEvent(media, ["loadedmetadata", "durationchange"], 2500);
  const metadataDuration = getMediaElementDuration(media, null);

  let probedDuration: number | null = null;
  try {
    media.currentTime = Number.MAX_SAFE_INTEGER;
    await waitForMediaEvent(media, ["durationchange", "seeked", "timeupdate"], 3000);
    probedDuration = getMediaElementDuration(media, null, 0, true);
  } catch (_error) {
    /* Fall back to the parsed or metadata duration below. */
  } finally {
    try {
      media.currentTime = 0;
    } catch (_error) {
      /* Some browsers can reject resetting an unseekable stream. */
    }
  }

  const parsedDuration = await parsedDurationPromise;
  if (parsedDuration && parsedDuration > 0) return parsedDuration;

  const bestDuration = Math.max(metadataDuration || 0, probedDuration || 0, fallbackDuration || 0);
  return bestDuration || null;
};

const STOP_LIBRARY_PLAYBACK_EVENT = "trebble:library-stop-inline-playback";
const PROJECT_CARD_VIDEO_THUMBNAIL_WIDTH = 640;
const LOW_QUALITY_STORED_THUMBNAIL_WIDTH = 480;

const ThumbnailIcon: React.FC<{ iconClass: string }> = ({ iconClass }) => (
  <span
    className={iconClass}
    style={{
      fontSize: 40,
      color: "var(--text-muted)",
      opacity: 0.5,
    }}
  />
);

interface VideoThumbnailProps {
  videoUrl: string;
  thumbnailUrl?: string;   // CloudFront URL with ?v= version token; undefined = generate on first view
  capsuleId?: string;      // needed to request presigned URL from backend
  bgColor: string;
  isPlaying?: boolean;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
}

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({
  videoUrl, thumbnailUrl, capsuleId, bgColor, isPlaying, videoRef
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const internalVideoRef = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [generatedThumbnail, setGeneratedThumbnail] = useState<string | null>(null);
  const [shouldUpgradeThumbnail, setShouldUpgradeThumbnail] = useState(false);
  const isGeneratingRef = useRef(false);

  const activeVideoRef = videoRef || internalVideoRef;
  const resolvedVideoUrl = getCloudFrontUrl(videoUrl);
  const displayThumbnail = generatedThumbnail || (thumbnailUrl ? getCloudFrontUrl(thumbnailUrl) : undefined);
  const handleThumbnailLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    if (!thumbnailUrl || generatedThumbnail) return;
    if (!videoUrl || !capsuleId) return;
    if (isRawS3VideoUrl(resolvedVideoUrl)) return;
    if (event.currentTarget.naturalWidth > 0 && event.currentTarget.naturalWidth < LOW_QUALITY_STORED_THUMBNAIL_WIDTH) {
      setShouldUpgradeThumbnail(true);
    }
  }, [thumbnailUrl, generatedThumbnail, videoUrl, resolvedVideoUrl, capsuleId]);

  // Observe scroll-into-view to trigger lazy self-heal generation
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => { observer.disconnect(); };
  }, []);

  // Self-heal: generate thumbnail on first view when none is stored or the stored one is low-res.
  useEffect(() => {
    if (!isVisible) return;
    if (displayThumbnail && !shouldUpgradeThumbnail) return;
    if (!videoUrl || !capsuleId) return;
    if (isRawS3VideoUrl(resolvedVideoUrl)) return;
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    let isMounted = true;

    (async () => {
      try {
        const { default: VideoUtilsHelper } = await import('../../helper/VideoUtilsHelper');
        const { default: TrebbleClientAPI } = await import('../../models/helper/TrebbleClientAPI');

        const blob = await VideoUtilsHelper.getInstance().extractFirstFrameAsBlob(resolvedVideoUrl, PROJECT_CARD_VIDEO_THUMBNAIL_WIDTH);
        if (!blob) return;

        const api = TrebbleClientAPI.getInstance();
        const presignResult = await api.getVideoThumbnailPresignedUrl(capsuleId);
        if (!presignResult || !presignResult.presignedUrl) return;
        const { presignedUrl, key } = presignResult;

        const uploadRes = await fetch(presignedUrl, {
          method: 'PUT',
          body: blob,
          headers: { 'Content-Type': 'image/jpeg' },
        });
        if (!uploadRes.ok) return;

        const s3Url = `https://fm.trebble.video.thumbnails.s3.amazonaws.com/${key}`;
        await api.recordVideoThumbnailUrl(capsuleId, s3Url);

        const { default: Utils } = await import('../../models/helper/Utils');
        const cfUrl = Utils.getInstance().getCloudFrontUrlIfApplicable(s3Url);
        if (isMounted) {
          setShouldUpgradeThumbnail(false);
          setGeneratedThumbnail(cfUrl + `?v=${Date.now()}`);
        }
      } catch (_) {
        isGeneratingRef.current = false; // allow retry on next intersection
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isVisible, displayThumbnail, shouldUpgradeThumbnail, videoUrl, resolvedVideoUrl, capsuleId]);

  // Start / stop playback when isPlaying changes
  useEffect(() => {
    const video = activeVideoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.muted = false;
      video.currentTime = 0;
      video.load();
      video.play().catch(() => { /* autoplay blocked */ });
    } else {
      video.pause();
      video.muted = true;
      /* Reset to thumbnail frame */
      video.currentTime = 0;
    }
  }, [isPlaying, activeVideoRef]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%", height: "100%",
        backgroundColor: bgColor,
        position: "relative",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {/* Thumbnail image — shows independently of video load state */}
      {displayThumbnail && (
        <img
          src={displayThumbnail}
          alt=""
          onLoad={handleThumbnailLoad}
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            objectFit: "cover",
          }}
        />
      )}

      {/* Placeholder icon — only while no thumbnail */}
      {!displayThumbnail && (
        <span
          className="pe-7s-video"
          style={{ fontSize: 40, color: "var(--text-muted)", opacity: 0.5 }}
        />
      )}

      {/* Video element — only mounted during explicit playback */}
      {isPlaying && (
        <video
          ref={activeVideoRef}
          src={resolvedVideoUrl}
          preload="none"
          muted
          playsInline
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            objectFit: "cover",
          }}
        />
      )}
    </div>
  );
};

/* ---- Inline Audio Player Bar ---- */
interface AudioPlayerBarProps {
  audioUrl: string;
  duration?: string;
  onDurationResolved?: (durationSeconds: number) => void;
  onClose: () => void;
}

const AudioPlayerBar: React.FC<AudioPlayerBarProps> = ({
  audioUrl,
  duration,
  onDurationResolved,
  onClose,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const onDurationResolvedRef = useRef(onDurationResolved);
  const resolvedDurationRef = useRef<number | null>(parseDurationLabel(duration));
  const [paused, setPaused] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState<number | null>(() => parseDurationLabel(duration));

  useEffect(() => {
    onDurationResolvedRef.current = onDurationResolved;
  }, [onDurationResolved]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    let cancelled = false;
    const resolvedAudioUrl = getCloudFrontUrl(audioUrl);

    const fallbackDuration = parseDurationLabel(duration);
    resolvedDurationRef.current = fallbackDuration;
    setTotalDuration(fallbackDuration);
    setCurrentTime(0);
    setPaused(false);
    audio.src = resolvedAudioUrl;
    audio.load();

    const updateDuration = (currentPlaybackTime?: number) => {
      const currentTime = getFinitePlaybackTime(currentPlaybackTime ?? audio.currentTime) || 0;
      const resolvedDuration = resolvedDurationRef.current;
      setTotalDuration(resolvedDuration && resolvedDuration >= currentTime - 0.5 ? resolvedDuration : null);
    };
    const onTimeUpdate = () => {
      const nextCurrentTime = getFinitePlaybackTime(audio.currentTime) || 0;
      setCurrentTime(nextCurrentTime);
      updateDuration(nextCurrentTime);
    };

    const startPlayback = async () => {
      const discoveredDuration = await discoverFiniteMediaDuration(audio, resolvedAudioUrl, fallbackDuration);
      if (cancelled) return;

      resolvedDurationRef.current = discoveredDuration;
      setTotalDuration(discoveredDuration);
      if (discoveredDuration && discoveredDuration > 0) {
        onDurationResolvedRef.current?.(discoveredDuration);
      }
      audio.addEventListener("timeupdate", onTimeUpdate);
      audio.addEventListener("ended", onEnded);
      audio.play().catch(() => {
        /* autoplay may be blocked; user can click play */
        setPaused(true);
      });
    };

    const onEnded = () => {
      setPaused(true);
      setCurrentTime(0);
      audio.currentTime = 0;
    };

    startPlayback();

    return () => {
      cancelled = true;
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.pause();
      audio.src = "";
    };
  }, [audioUrl, duration]);

  const togglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play();
      setPaused(false);
    } else {
      audio.pause();
      setPaused(true);
    }
  };

  const handleProgressClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const audio = audioRef.current;
    const bar = progressBarRef.current;
    if (!audio || !bar || !totalDuration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * totalDuration;
    setCurrentTime(getFinitePlaybackTime(audio.currentTime) || 0);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = "";
    }
    onClose();
  };

  const progress = totalDuration ? Math.max(0, Math.min(100, (currentTime / totalDuration) * 100)) : 0;

  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: 36,
        background: "rgba(0,0,0,0.80)",
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "0 8px",
        zIndex: 5,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <audio ref={audioRef} preload="metadata" />

      {/* Play / Pause */}
      <button
        type="button"
        onClick={togglePlayPause}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
        aria-label={paused ? "Play" : "Pause"}
      >
        {paused ? <PlayIcon /> : <PauseIcon />}
      </button>

      {/* Progress bar */}
      <div
        ref={progressBarRef}
        onClick={handleProgressClick}
        style={{
          flex: 1,
          height: 4,
          background: "rgba(255,255,255,0.25)",
          cursor: "pointer",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: `${progress}%`,
            background: "var(--trebble-primary, #EC7F54)",
            transition: "width 0.1s linear",
          }}
        />
      </div>

      {/* Time display */}
      <span
        style={{
          fontSize: 11,
          color: "var(--text-light)",
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          flexShrink: 0,
          minWidth: 70,
          textAlign: "right",
        }}
      >
        {formatTime(currentTime)}{totalDuration ? ` / ${formatTime(totalDuration)}` : ""}
      </span>

      {/* Close button */}
      <button
        type="button"
        onClick={handleClose}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          opacity: 0.8,
        }}
        aria-label="Close player"
      >
        <CloseIcon />
      </button>
    </div>
  );
};

/* Thumbnail colors — Trebble speaker accent palette at 0.2 opacity */
const THUMBNAIL_COLORS = [
  "rgba(168, 226, 29, 0.2)",   // green
  "rgba(197, 163, 251, 0.2)",  // purple
  "rgba(255, 167, 212, 0.2)",  // pink
  "rgba(130, 217, 227, 0.2)",  // teal
  "rgba(112, 160, 152, 0.2)",  // accent complementary
  "rgba(74, 144, 217, 0.2)",   // blue
  "rgba(207, 239, 130, 0.2)",  // light green
  "rgba(212, 187, 252, 0.2)",  // light purple
  "rgba(173, 230, 237, 0.2)",  // light teal
];

const getThumbnailColor = (id: string): string => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  }
  return THUMBNAIL_COLORS[Math.abs(hash) % THUMBNAIL_COLORS.length];
};

const getTypeLabel = (
  type: "audio" | "video" | "image" | "vector",
  ti18n?: Record<string, string>
): string => {
  if (type === "video") {
    return t(ti18n, "VIDEO");
  }

  if (type === "image") {
    return t(ti18n, "IMAGE");
  }

  if (type === "vector") {
    return t(ti18n, "VECTOR");
  }

  return t(ti18n, "AUDIO");
};

const ProjectCard: React.FC<ProjectCardProps> = ({
  id,
  capsuleId,
  name,
  type,
  duration,
  date,
  creator,
  thumbnailColor,
  thumbnailUrl,
  videoUrl,
  audioUrl,
  previewUrl,
  status = "ready",
  viewMode = "grid",
  onPlay,
  onDownload,
  onMenuAction,
  onClick,
  ti18n,
  showDownloadButton = true,
  menuActions,
  primaryActionLabel,
  primaryActionIconClassName = "pe-7s-pen",
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [resolvedDurationLabel, setResolvedDurationLabel] = useState<string | undefined>();
  const videoRef = useRef<HTMLVideoElement>(null);
  const bgColor = thumbnailColor || getThumbnailColor(id);

  const isProcessing = status === "processing";

  const canPlayInline = type === "audio" ? !!audioUrl : !!(type === "video" && videoUrl);
  const shouldShowPlaybackOverlay = (type === "audio" || type === "video");
  const displayDuration = resolvedDurationLabel || duration;

  useEffect(() => {
    setResolvedDurationLabel(undefined);
  }, [audioUrl, duration, id]);

  const handleDurationResolved = useCallback((durationSeconds: number) => {
    setResolvedDurationLabel(formatTime(durationSeconds));
  }, []);

  const handlePlaybackButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }

    if (canPlayInline) {
      // Stop any other cards currently playing inline before starting this one
      window.dispatchEvent(new CustomEvent(STOP_LIBRARY_PLAYBACK_EVENT));
      setIsPlaying(true);
    } else {
      onPlay(id);
    }
  };

  const handleStopPlayback = () => {
    setIsPlaying(false);
  };

  const handlePlaybackOverlayClick = (e: React.MouseEvent) => {
    if (!isPlaying) return;
    e.stopPropagation();
    setIsPlaying(false);
  };

  useEffect(() => {
    const stopPlayback = () => setIsPlaying(false);
    window.addEventListener(STOP_LIBRARY_PLAYBACK_EVENT, stopPlayback);
    return () => {
      window.removeEventListener(STOP_LIBRARY_PLAYBACK_EVENT, stopPlayback);
    };
  }, []);

  const allMenuItems: Record<ProjectCardMenuAction, { key: string; label: string; icon: React.ReactNode }> = {
    edit: {
      key: "edit",
      label: t(ti18n, "EDIT"),
      icon: <span className="pe-7s-pen" style={{ fontSize: 18 }} />,
    },
    duplicate: {
      key: "duplicate",
      label: t(ti18n, "DUPLICATE_AND_EDIT"),
      icon: <span className="pe-7s-copy-file" style={{ fontSize: 18 }} />,
    },
    transcribe: {
      key: "transcribe",
      // Newspaper icon mirrors the master capsule context-menu choice
      // (see PR #1165) so the entry is recognizable across surfaces.
      label: t(ti18n, "TRANSCRIBE_AND_EDIT"),
      icon: <span className="pe-7s-news-paper" style={{ fontSize: 18 }} />,
    },
    download: {
      key: "download",
      label: t(ti18n, "DOWNLOAD_AS"),
      icon: <span className="pe-7s-download" style={{ fontSize: 18 }} />,
    },
    share: {
      key: "share",
      label: t(ti18n, "SHARE"),
      icon: <span className="pe-7s-share" style={{ fontSize: 18 }} />,
    },
    comments: {
      key: "comments",
      label: t(ti18n, "COMMENTS"),
      icon: <span className="pe-7s-comment" style={{ fontSize: 18 }} />,
    },
    move: {
      key: "move",
      label: t(ti18n, "MOVE_TO_FOLDER"),
      icon: <span className="pe-7s-folder" style={{ fontSize: 18 }} />,
    },
    delete: {
      key: "delete",
      label: t(ti18n, "DELETE"),
      icon: <span className="pe-7s-trash" style={{ fontSize: 18 }} />,
    },
  };

  const requestedMenuActions: ProjectCardMenuAction[] = Array.isArray(menuActions)
    ? menuActions
    : [...PROJECT_CARD_MENU_ACTIONS.filter((action) => action !== "transcribe")];

  const visibleMenuItems = requestedMenuActions
    .map((actionKey) => allMenuItems[actionKey])
    .filter(Boolean);

  const menuItems: MenuProps = {
    items: visibleMenuItems,
    onClick: (info) => {
      info.domEvent.stopPropagation();
      onMenuAction(id, String(info.key));
      setMenuOpen(false);
    },
  };
  const shouldShowMenu = visibleMenuItems.length > 0;

  const renderThumbnailContent = () => {
    if (isProcessing) {
      return (
        <div
          style={{
            width: "100%",
            aspectRatio: "16 / 9",
            backgroundColor: bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="library-spinner" />
        </div>
      );
    }

    if (type === "audio") {
      return (
        <div
          style={{
            width: "100%",
            aspectRatio: "16 / 9",
            backgroundColor: bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <ThumbnailIcon iconClass="pe-7s-music" />
          {isPlaying && audioUrl && (
            <AudioPlayerBar
              audioUrl={audioUrl}
              duration={duration}
              onDurationResolved={handleDurationResolved}
              onClose={handleStopPlayback}
            />
          )}
        </div>
      );
    }

    if ((type === "image" || type === "vector") && previewUrl) {
      return (
        <div
          style={{
            width: "100%",
            aspectRatio: "16 / 9",
            backgroundColor: bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {imageLoadFailed ? (
            <i
              className={type === "vector" ? "pe-7s-paint-bucket" : "pe-7s-photo"}
              style={{ fontSize: 36, color: "rgba(255,255,255,0.5)", WebkitTextStroke: "0.5px" }}
            />
          ) : (
            <img
              src={getCloudFrontUrl(previewUrl)}
              alt={name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={() => setImageLoadFailed(true)}
            />
          )}
        </div>
      );
    }

    if (type === "image") {
      return (
        <div
          style={{
            width: "100%",
            aspectRatio: "16 / 9",
            backgroundColor: bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ThumbnailIcon iconClass="pe-7s-photo" />
        </div>
      );
    }

    if (type === "vector") {
      return (
        <div
          style={{
            width: "100%",
            aspectRatio: "16 / 9",
            backgroundColor: bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ThumbnailIcon iconClass="pe-7s-paint-bucket" />
        </div>
      );
    }

    // Video with thumbnail
    if (type === "video" && videoUrl) {
      return (
        <div
          style={{
            width: "100%",
            aspectRatio: "16 / 9",
            backgroundColor: bgColor,
            position: "relative",
          }}
        >
          <VideoThumbnail
            videoUrl={videoUrl}
            thumbnailUrl={thumbnailUrl}
            capsuleId={capsuleId || id}
            bgColor={bgColor}
            isPlaying={isPlaying}
            videoRef={videoRef}
          />
        </div>
      );
    }

    // Video fallback (no videoUrl)
    return (
      <div
        style={{
          width: "100%",
          aspectRatio: "16 / 9",
          backgroundColor: bgColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ThumbnailIcon iconClass="pe-7s-video" />
      </div>
    );
  };

  /* ---- List view: compact horizontal row ---- */
  if (viewMode === "list") {
    const renderListThumbnail = () => {
      if (isProcessing) {
        return (
          <div style={{ width: "100%", height: "100%", backgroundColor: bgColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div className="library-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
          </div>
        );
      }
      if (type === "video" && videoUrl) {
        return <VideoThumbnail videoUrl={videoUrl} thumbnailUrl={thumbnailUrl} capsuleId={capsuleId || id} bgColor={bgColor} />;
      }
      if (type === "audio") {
        return (
          <div style={{ width: "100%", height: "100%", backgroundColor: bgColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span className="pe-7s-music" style={{ fontSize: 18, color: "var(--text-muted)", opacity: 0.5 }} />
          </div>
        );
      }
      if ((type === "image" || type === "vector") && previewUrl) {
        return (
          <div style={{ width: "100%", height: "100%", backgroundColor: bgColor, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {imageLoadFailed ? (
              <span
                className={type === "vector" ? "pe-7s-paint-bucket" : "pe-7s-photo"}
                style={{ fontSize: 18, color: "var(--text-muted)", opacity: 0.5 }}
              />
            ) : (
              <img
                src={getCloudFrontUrl(previewUrl)}
                alt={name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={() => setImageLoadFailed(true)}
              />
            )}
          </div>
        );
      }
      if (type === "image") {
        return (
          <div style={{ width: "100%", height: "100%", backgroundColor: bgColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span className="pe-7s-photo" style={{ fontSize: 18, color: "var(--text-muted)", opacity: 0.5 }} />
          </div>
        );
      }
      if (type === "vector") {
        return (
          <div style={{ width: "100%", height: "100%", backgroundColor: bgColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span className="pe-7s-paint-bucket" style={{ fontSize: 18, color: "var(--text-muted)", opacity: 0.5 }} />
          </div>
        );
      }
      return (
        <div style={{ width: "100%", height: "100%", backgroundColor: bgColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span className="pe-7s-video" style={{ fontSize: 18, color: "var(--text-muted)", opacity: 0.5 }} />
        </div>
      );
    };

    return (
      <div
        className="library-project-card library-project-card--list"
        style={{
          backgroundColor: "var(--bg-default, #fff)",
          borderBottom: "1px solid var(--border-default-color, #e5e5e5)",
          cursor: "pointer",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
        }}
        onClick={() => onClick(id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(id);
          }
        }}
      >
        {/* Small thumbnail */}
        <div style={{ width: 80, height: 44, flexShrink: 0, position: "relative", overflow: "hidden" }}>
          {renderListThumbnail()}
          {displayDuration && !isProcessing && (
            <span className="library-duration-badge">{displayDuration}</span>
          )}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0, padding: "6px 12px" }}>
          <p style={{
            margin: 0,
            fontSize: "var(--text-base)",
            fontWeight: 900,
            color: "var(--text-default-color, #1a1a1a)",
            lineHeight: "1.3",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {name}
          </p>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 2,
            fontSize: "var(--text-sm, 13px)",
            color: "var(--text-muted, #999)",
            minWidth: 0,
          }}>
            {creator && (
              <>
                <span
                  style={{
                    width: 16,
                    height: 16,
                    backgroundColor: creator.color,
                    color: "#fff",
                    fontSize: 8,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {creator.initials}
                </span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{creator.name}</span>
                <span style={{ flexShrink: 0 }}>&middot;</span>
              </>
            )}
            <span style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
              {getTypeLabel(type, ti18n)}
              {date && <> &middot; {date}</>}
              {displayDuration && <> &middot; {displayDuration}</>}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="library-card-actions" style={{ marginLeft: "auto", flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="library-edit-btn"
            onClick={() => onClick(id)}
          >
            {primaryActionLabel || t(ti18n, "EDIT")}
            <span className={primaryActionIconClassName} />
          </button>
          <div style={{ marginLeft: "auto", display: "flex", gap: 2 }}>
            {showDownloadButton && (
              <TrebbleTooltip title={t(ti18n, "DOWNLOAD")} useEditorTheme={false}>
                <button
                  type="button"
                  className="library-action-btn"
                  onClick={() => onDownload(id)}
                  aria-label={t(ti18n, "DOWNLOAD")}
                >
                  <span className="pe-7s-download" />
                </button>
              </TrebbleTooltip>
            )}
            {shouldShowMenu && (
              <TrebbleDropdown
                useEditorTheme={false}
                menu={menuItems}
                trigger={["click"]}
                open={menuOpen}
                onOpenChange={(open) => setMenuOpen(open)}
                overlayClassName="library-card-dropdown"
              >
                <button
                  type="button"
                  className="library-action-btn"
                  aria-label={t(ti18n, "MORE_OPTIONS")}
                >
                  <span className="pe-7s-more" />
                </button>
              </TrebbleDropdown>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ---- Grid view: card with thumbnail on top ---- */
  return (
    <div
      className="library-project-card"
      style={{
        backgroundColor: "var(--bg-default, #fff)",
        border: "1px solid var(--border-default-color, #e5e5e5)",
        cursor: "pointer",
        overflow: "hidden",
      }}
      onClick={() => onClick(id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(id);
        }
      }}
    >
      {/* Thumbnail area */}
      <div className="library-card-thumbnail">
        {renderThumbnailContent()}

        {/* Hover overlay — centered playback toggle */}
        {!isProcessing && shouldShowPlaybackOverlay && (
          <div
            className={`library-card-overlay${isPlaying ? " library-card-overlay--active" : ""}`}
            onClick={handlePlaybackOverlayClick}
          >
            <button
              type="button"
              className="library-play-button"
              onClick={handlePlaybackButtonClick}
              aria-label={isPlaying ? t(ti18n, "STOP") : t(ti18n, "PLAY")}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
          </div>
        )}

        {/* Duration badge */}
        {displayDuration && !isProcessing && (
          <span className="library-duration-badge">{displayDuration}</span>
        )}

        {/* Processing label */}
        {isProcessing && (
          <span className="library-duration-badge">
            {t(ti18n, "PROCESSING")}
          </span>
        )}
      </div>

      {/* Card info */}
      <div style={{ padding: "10px 12px 4px" }}>
        <p
          style={{
            margin: 0,
            fontSize: "var(--text-base)",
            fontWeight: 900,
            color: "var(--text-default-color, #1a1a1a)",
            lineHeight: "1.4",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {name}
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 4,
            minWidth: 0,
            fontSize: "var(--text-sm, 13px)",
            color: "var(--text-muted, #999)",
          }}
        >
          {creator && (
            <>
              <span
                style={{
                  width: 16,
                  height: 16,
                  backgroundColor: creator.color,
                  color: "#fff",
                  fontSize: 8,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {creator.initials}
              </span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{creator.name}</span>
              <span style={{ flexShrink: 0 }}>&middot;</span>
            </>
          )}
          <span style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
            {date}
            {displayDuration && <> &middot; {displayDuration}</>}
          </span>
        </div>
      </div>

      {/* Action row — Edit left, Download + More right, all in one aligned container */}
      <div className="library-card-actions" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="library-edit-btn"
          onClick={() => onClick(id)}
        >
          {primaryActionLabel || t(ti18n, "EDIT")}
          <span className={primaryActionIconClassName} />
        </button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 2 }}>
          {showDownloadButton && (
            <TrebbleTooltip title={t(ti18n, "DOWNLOAD")} useEditorTheme={false}>
              <button
                type="button"
                className="library-action-btn"
                onClick={() => onDownload(id)}
                aria-label={t(ti18n, "DOWNLOAD")}
              >
                <span className="pe-7s-download" />
              </button>
            </TrebbleTooltip>
          )}
          {shouldShowMenu && (
            <TrebbleDropdown
              useEditorTheme={false}
              menu={menuItems}
              trigger={["click"]}
              open={menuOpen}
              onOpenChange={(open) => setMenuOpen(open)}
              overlayClassName="library-card-dropdown"
            >
              <button
                type="button"
                className="library-action-btn"
                aria-label={t(ti18n, "MORE_OPTIONS")}
              >
                <span className="pe-7s-more" />
              </button>
            </TrebbleDropdown>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
